import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useNavigate, useParams } from "@tanstack/react-router"
import {
	IconCheckDoubleOutline18,
	IconCircleCheckOutline18,
	IconDotsOutline18,
	IconEnvelopeOpenOutline18,
	IconGear2Outline18,
	IconTrashOutline18,
	IconTrashXmarkOutline18,
	IconXmarkOutline18,
} from "nucleo-ui-outline-18"
import { useMedia } from "react-use"

import type { NotificationTarget } from "@productlane/lib/src/browser"
import { cn } from "@productlane/lib/src/cn"
import { ShortcutSymbols } from "@productlane/lib/src/shortcut-symbols"
import {
	Button,
	IconButton,
	Loader,
	Shortcut,
	Tabs,
	TabsList,
	TabsTrigger,
	Tooltip,
} from "@productlane/ui"
import {
	DropdownMenu,
	DropdownMenuItem,
	DropdownMenuSeparator,
} from "@productlane/ui/src/components/internal/dropdown"
import { NOTIFICATION_INBOX_MAX_LIMIT } from "@productlane/zero/queries"

import type { NotificationGroup } from "./group-notifications"
import type { NotificationNav } from "./notification-detail"
import type { NotificationListView } from "./notification-filter-state"
import type { RowClickModifiers } from "./notification-row"
import type { DoneThread } from "./use-mark-threads-done"
import type { NotificationListRow } from "./use-notifications"
import type { RouterOutputs } from "@/lib/api"
import { MetaInfo } from "@/components/meta-info"
import {
	INBOX_LIST_WIDTH_CLASSES,
	PANEL_SPECS,
	panelWidthStyle,
} from "@/components/panel-resize/panel-resize"
import { PanelResizeGutter } from "@/components/panel-resize/panel-resize-handle"
import { toast } from "@/components/toast"
import { useShortcut } from "@/lib"
import { api } from "@/lib/api"
import { useEscapeClearsSelection } from "@/lib/hooks/use-escape-clears-selection"
import { useCopilotState } from "@/lib/states/copilot-state"
import { usePanelWidthState } from "@/lib/states/panel-width-state"
import { useRole } from "@/lib/useRole"
import { CopilotOverlay } from "@/screens/app/threads/components/right-bar/ai-chat/copilot-overlay"
import {
	NAV_TOGGLE_CLEARANCE,
	TOP_BAR_HEIGHT,
	TOP_BAR_PADDING,
} from "@/screens/app/top-bar-tokens"
import { NOTIFICATIONS_COPY as copy } from "./copy"
import { groupNotifications } from "./group-notifications"
import { useKeptReadRows } from "./kept-read-rows"
import { useNotificationCommands } from "./notification-commands"
import { NotificationListContextMenu } from "./notification-context-menu"
import { NotificationDetail } from "./notification-detail"
import {
	NotificationFilter,
	NotificationFilterChips,
} from "./notification-filter"
import {
	hasNotificationFilters,
	listView,
	NOTIFICATION_LIST_VIEWS,
	notificationInboxArgs,
	useNotificationFilterState,
} from "./notification-filter-state"
import { NotificationListEmpty } from "./notification-list-empty"
import {
	neighborKeys,
	notificationGroupPath,
	notificationText,
} from "./notification-presentation"
import { NotificationRow } from "./notification-row"
import {
	actionTargets,
	cursorAfterRemoval,
	donePlan,
	extendSelection,
	rangeKeys,
	selectionTarget,
} from "./notification-selection"
import {
	notificationDestination,
	notificationKeyTarget,
	notificationTargetPath,
	readNotificationTarget,
} from "./target-to-path"
import { useMarkThreadsDone } from "./use-mark-threads-done"
import {
	NOTIFICATION_PAGE_SIZE,
	useNotificationActions,
	useNotificationInbox,
	useUnreadMentions,
	useUnreadNotifications,
} from "./use-notifications"

const OPEN_OVERLAY =
	'[role="dialog"][data-state="open"], [role="menu"][data-state="open"], [role="listbox"][data-state="open"]'
const WORKSPACE_COPILOT = { kind: "workspace" } as const
const SETTINGS_PATH = "/settings/notifications/inbox"
// Matches the server mutator's cap per call.
const MAX_IDS_PER_CALL = 500

type Group = NotificationGroup<NotificationListRow>
type FetchedGroup = RouterOutputs["notification"]["selectAll"][number]

function MenuShortcut({ keys }: { keys: string[] }) {
	return (
		<span className="flex items-center gap-1 tracking-normal">
			{keys.map((key) => (
				<Shortcut key={key}>{key}</Shortcut>
			))}
		</span>
	)
}

function chunk<T>(items: T[], size: number): T[][] {
	const out: T[][] = []
	for (let i = 0; i < items.length; i += size)
		out.push(items.slice(i, i + size))
	return out
}

export function NotificationsScreen() {
	const navigate = useNavigate()
	const { key: selectedKey } = useParams({ strict: false })
	// Below this width the list is the page and a row opens its target instead.
	const isSplit = useMedia("(min-width: 1024px)")
	const listWidth = usePanelWidthState((s) => s.widths["inbox-list"])
	const listPanelRef = useRef<HTMLElement>(null)
	const detailPanelRef = useRef<HTMLDivElement>(null)
	const filters = useNotificationFilterState()
	const view = listView(filters.view)
	const [limit, setLimit] = useState(NOTIFICATION_PAGE_SIZE)
	const args = notificationInboxArgs(filters, limit)
	const { rows: liveRows, isLoaded } = useNotificationInbox(args)
	const { formattedCount: unreadCount } = useUnreadNotifications()
	const { formattedCount: mentionCount } = useUnreadMentions()
	const viewCounts: Partial<Record<NotificationListView, string>> = {
		unread: unreadCount,
		mentions: mentionCount,
	}
	const actions = useNotificationActions()
	const utils = api.useUtils()
	const { limit: _limit, ...filter } = args
	const filterKey = JSON.stringify(filter)
	const { rows, keep, forget, clear } = useKeptReadRows(
		liveRows,
		view === "unread" ? filterKey : null,
	)

	const groups = useMemo(() => groupNotifications(rows), [rows])
	const keys = useMemo(() => groups.map((g) => g.key), [groups])
	const groupsByKey = useMemo(
		() => new Map(groups.map((g) => [g.key, g])),
		[groups],
	)
	const getMenuGroup = useCallback(
		(key: string) => groupsByKey.get(key),
		[groupsByKey],
	)
	// A row read in another tab leaves the Unread view; keep its detail open.
	const pinned = useRef<Group | undefined>(undefined)
	const found = selectedKey ? groupsByKey.get(selectedKey) : undefined
	if (found) pinned.current = found
	else if (pinned.current?.key !== selectedKey) pinned.current = undefined
	const selected = selectedKey ? (found ?? pinned.current) : undefined
	// A link to a group the viewer has no loaded rows in (a teammate's, or past the page) still opens.
	// Held per key, so a refetch from "Show older" or a tab switch doesn't blank the pane.
	const shared = useRef<{ key: string; target: NotificationTarget } | null>(
		null,
	)
	if (!selectedKey || selected) shared.current = null
	else if (isLoaded && shared.current?.key !== selectedKey) {
		shared.current = {
			key: selectedKey,
			target: notificationKeyTarget(selectedKey),
		}
	}
	const sharedTarget =
		shared.current && shared.current.key === selectedKey
			? shared.current.target
			: null
	const { prev, next } = neighborKeys(keys, selectedKey)
	const hasFilters = hasNotificationFilters(filters)

	const [checkedState, setChecked] = useState<Set<string>>(() => new Set())
	// Cmd+A's groups past the loaded page, valid only for the filters they were fetched with.
	const [fetched, setFetched] = useState<{
		filterKey: string
		groups: Map<string, FetchedGroup>
	} | null>(null)
	const fetchedGroups =
		fetched?.filterKey === filterKey ? fetched.groups : undefined
	// Rows that left the list (deleted, filtered out) drop out of the selection.
	const checked = useMemo(
		() =>
			new Set(
				[...checkedState].filter(
					(k) => groupsByKey.has(k) || fetchedGroups?.has(k),
				),
			),
		[checkedState, groupsByKey, fetchedGroups],
	)
	const anchor = useRef<string | null>(null)
	// Bumped by any manual selection change, so a slow Cmd+A fetch can't overwrite it.
	const selectAllRequest = useRef(0)

	const run = useCallback((promise: Promise<unknown>) => {
		promise.catch(() => toast.error({ title: copy.toasts.failed }))
	}, [])

	const openKey = useCallback(
		(key: string | null) => {
			void navigate({
				href: key ? notificationGroupPath(key) : "/notifications",
				replace: true,
			})
		},
		[navigate],
	)

	// Reads a group once, when it is opened; marking it unread afterwards sticks.
	const lastOpened = useRef<string | null>(null)
	useEffect(() => {
		if (!selected || lastOpened.current === selected.key) return
		lastOpened.current = selected.key
		if (selected.unreadIds.length > 0) {
			keep(selected.unreadIds)
			run(actions.setRead(selected.unreadIds, true))
		}
	}, [selected, keep, actions, run])

	// Without the split there is no pane, so a shared link goes to its own page.
	useEffect(() => {
		if (isSplit || !sharedTarget) return
		const path = notificationTargetPath(sharedTarget)
		if (path) void navigate({ href: path, replace: true })
	}, [isSplit, sharedTarget, navigate])

	// Keyboard moves can run past the visible rows. Focus follows the cursor, or a
	// clicked row keeps its focus-visible highlight next to the cursor's.
	useEffect(() => {
		if (!selectedKey) return
		const row = document.querySelector<HTMLElement>(
			`[data-notification-group="${CSS.escape(selectedKey)}"]`,
		)
		row?.scrollIntoView({ block: "nearest" })
		const focused = document.activeElement
		if (
			row &&
			focused instanceof HTMLElement &&
			focused !== row &&
			focused.closest("[data-notification-group]")
		) {
			row.focus({ preventScroll: true })
		}
	}, [selectedKey])

	const open = useCallback(
		(group: Group) => {
			const latest = group.rows[0]
			if (!latest) return
			const destination = notificationDestination(latest)
			if (isSplit && destination.kind !== "page") {
				openKey(group.key)
				return
			}
			if (group.unreadIds.length > 0) {
				keep(group.unreadIds)
				run(actions.setRead(group.unreadIds, true))
			}
			const path =
				destination.kind === "page"
					? destination.path
					: notificationTargetPath(readNotificationTarget(latest.payload))
			if (path) void navigate({ href: path })
		},
		[isSplit, openKey, keep, actions, run, navigate],
	)

	const toggleChecked = useCallback((key: string) => {
		anchor.current = key
		selectAllRequest.current++
		setChecked((prev) => {
			const nextSet = new Set(prev)
			if (nextSet.has(key)) nextSet.delete(key)
			else nextSet.add(key)
			return nextSet
		})
	}, [])

	const onRowClick = useCallback(
		(group: Group, { shift, toggle }: RowClickModifiers) => {
			if (toggle) {
				toggleChecked(group.key)
				return
			}
			if (shift) {
				const range = rangeKeys(
					keys,
					anchor.current ?? selectedKey ?? null,
					group.key,
				)
				setChecked((prev) => new Set([...prev, ...range]))
				return
			}
			open(group)
		},
		[toggleChecked, keys, selectedKey, open],
	)

	const clearSelection = useCallback(() => {
		selectAllRequest.current++
		setChecked(new Set())
		setFetched(null)
	}, [])

	// A bulk delete can empty fetched groups the list never loaded.
	const removeInBulk = useCallback(
		(promise: Promise<unknown>) => {
			clearSelection()
			clear()
			run(promise)
		},
		[clearSelection, clear, run],
	)

	const targetsFor = useCallback(
		(targetKeys: string[]) =>
			targetKeys.flatMap((key) => {
				const group =
					groupsByKey.get(key) ??
					(pinned.current?.key === key ? pinned.current : undefined)
				const target = selectionTarget(group, fetchedGroups?.get(key))
				return target ? [target] : []
			}),
		[groupsByKey, fetchedGroups],
	)

	const removeKeys = useCallback(
		(targetKeys: string[]) => {
			if (targetKeys.length === 0) return
			const removed = new Set(targetKeys)
			if (selectedKey && removed.has(selectedKey)) {
				openKey(cursorAfterRemoval(keys, removed, selectedKey))
			}
			setChecked((prev) => new Set([...prev].filter((k) => !removed.has(k))))
			const ids = targetsFor(targetKeys).flatMap((t) => t.ids)
			forget(ids)
			for (const batch of chunk(ids, MAX_IDS_PER_CALL)) {
				run(actions.remove(batch))
			}
		},
		[selectedKey, openKey, keys, targetsFor, forget, actions, run],
	)

	const markThreadsDone = useMarkThreadsDone()
	// E: archives the target groups and marks done only the open group's thread.
	const doneKeys = useCallback(
		(targetKeys: string[]) => {
			if (targetKeys.length === 0) return
			const removed = new Set(targetKeys)
			if (selectedKey && removed.has(selectedKey)) {
				openKey(cursorAfterRemoval(keys, removed, selectedKey))
			}
			setChecked((prev) => new Set([...prev].filter((k) => !removed.has(k))))
			// Loaded rows carry the whole thread (title, snooze) for the toast and its undo.
			const threadsById = new Map<string, DoneThread>()
			const candidates = targetKeys.flatMap((key) => {
				const target = targetsFor([key])[0]
				if (!target) return []
				const group =
					groupsByKey.get(key) ??
					(pinned.current?.key === key ? pinned.current : undefined)
				const thread =
					group?.rows.find((r) => r.thread)?.thread ??
					fetchedGroups?.get(key)?.thread ??
					null
				if (thread) threadsById.set(thread.id, thread)
				return [{ key, ids: target.ids, thread }]
			})
			const plan = donePlan(candidates, selectedKey)
			forget(plan.ids)
			const archive = (archived: boolean) =>
				Promise.all(
					chunk(plan.ids, MAX_IDS_PER_CALL).map((batch) =>
						actions.setArchived(batch, archived),
					),
				)
			run(archive(true))
			const threads = plan.threadIds.flatMap((id) => {
				const thread = threadsById.get(id)
				return thread ? [thread] : []
			})
			run(markThreadsDone(threads, () => archive(false)))
		},
		[
			selectedKey,
			openKey,
			keys,
			targetsFor,
			groupsByKey,
			fetchedGroups,
			forget,
			actions,
			run,
			markThreadsDone,
		],
	)

	// Unread anywhere in the set marks it all read; an all-read set goes back to unread.
	const toggleReadKeys = useCallback(
		(targetKeys: string[]) => {
			const targets = targetsFor(targetKeys)
			if (targets.length === 0) return
			const anyUnread = targets.some((t) => t.unreadIds.length > 0)
			const ids = targets.flatMap((t) => (anyUnread ? t.unreadIds : t.ids))
			if (anyUnread) keep(ids)
			for (const batch of chunk(ids, MAX_IDS_PER_CALL)) {
				run(actions.setRead(batch, anyUnread))
			}
		},
		[targetsFor, keep, actions, run],
	)

	// The list holds one page; past it the server lists every match, so actions reach all of them.
	const selectAll = useCallback(() => {
		const request = ++selectAllRequest.current
		setChecked(new Set(keys))
		if (liveRows.length < limit) return
		const requestedFor = filterKey
		utils.notification.selectAll
			.fetch(filter)
			.then((groups) => {
				if (request !== selectAllRequest.current) return
				setFetched({
					filterKey: requestedFor,
					groups: new Map(groups.map((g) => [g.key, g])),
				})
				setChecked((prev) => new Set([...prev, ...groups.map((g) => g.key)]))
			})
			.catch(() => toast.error({ title: copy.toasts.failed }))
	}, [keys, liveRows.length, limit, filterKey, filter, utils])

	const markAllRead = useCallback(() => {
		keep(liveRows.map((r) => r.id))
		run(actions.markAllRead())
	}, [keep, liveRows, actions, run])

	const moveCursor = useCallback(
		(direction: 1 | -1) => {
			const target = found
				? direction === 1
					? next
					: prev
				: direction === 1
					? (keys[0] ?? null)
					: (keys[keys.length - 1] ?? null)
			if (target) openKey(target)
		},
		[found, next, prev, keys, openKey],
	)

	const extend = useCallback(
		(direction: 1 | -1) => {
			const result = extendSelection(keys, checked, found?.key, direction)
			setChecked(result.checked)
			if (result.cursor) openKey(result.cursor)
		},
		[keys, checked, found, openKey],
	)

	const nav = useMemo<NotificationNav>(
		() => ({
			nextKey: next,
			prevKey: prev,
			goNext: () => next && openKey(next),
			goPrev: () => prev && openKey(prev),
		}),
		[next, prev, openKey],
	)

	useEffect(() => {
		const onKeyDown = (event: KeyboardEvent) => {
			if (!(event.metaKey || event.ctrlKey) || event.altKey || event.shiftKey)
				return
			if (event.key !== "ArrowUp" && event.key !== "ArrowDown") return
			if (
				keys.length === 0 ||
				selectedKey ||
				document.querySelector(OPEN_OVERLAY)
			)
				return
			const target = event.target
			if (
				target instanceof HTMLElement &&
				target.closest(
					'input, textarea, select, [contenteditable]:not([contenteditable="false"])',
				)
			)
				return
			event.preventDefault()
			event.stopPropagation()
			openKey(event.key === "ArrowUp" ? keys[0]! : keys[keys.length - 1]!)
		}
		window.addEventListener("keydown", onKeyDown, true)
		return () => window.removeEventListener("keydown", onKeyDown, true)
	}, [keys, openKey, selectedKey])
	const toggleCopilot = useCopilotState((s) => s.toggle)
	const canSeeAiChat = useRole().role !== "VIEWER"
	useShortcut("down, j", () => moveCursor(1), { preventDefault: true })
	useShortcut("up, k", () => moveCursor(-1), { preventDefault: true })
	useShortcut("shift+down, shift+j", () => extend(1), { preventDefault: true })
	useShortcut("shift+up, shift+k", () => extend(-1), { preventDefault: true })
	useShortcut("x", () => {
		if (selectedKey && groupsByKey.has(selectedKey)) toggleChecked(selectedKey)
	})
	// Cmd+A on Mac, Ctrl+A elsewhere.
	useShortcut("mod+a", selectAll, {
		preventDefault: true,
		enabled: keys.length > 0,
	})
	useShortcut("e", () => doneKeys(actionTargets(checked, selectedKey)))
	useShortcut("backspace, delete", () =>
		removeKeys(actionTargets(checked, selectedKey)),
	)
	useShortcut("u", () => toggleReadKeys(actionTargets(checked, selectedKey)))
	useShortcut("alt+u", markAllRead, {
		preventDefault: true,
	})
	useShortcut("shift+backspace", () => removeInBulk(actions.removeAllRead()), {
		preventDefault: true,
	})
	useEscapeClearsSelection(checked.size > 0, clearSelection)
	const threadOpen =
		(selected
			? readNotificationTarget(selected.rows[0]?.payload)
			: sharedTarget
		)?.kind === "thread"
	useShortcut("mod+j", () => toggleCopilot(WORKSPACE_COPILOT), {
		preventDefault: true,
		enabled: isSplit && !threadOpen && canSeeAiChat,
		enableOnFormTags: true,
		enableOnContentEditable: true,
	})

	useEffect(() => {
		const latest = found?.rows[0]
		useNotificationCommands.setState({
			commands: {
				current: latest
					? {
							title: notificationText(latest).title,
							unread: found.unreadIds.length > 0,
						}
					: null,
				toggleRead: () => found && toggleReadKeys([found.key]),
				remove: () => found && removeKeys([found.key]),
				markAllRead,
				deleteAll: () => removeInBulk(actions.removeAll()),
				deleteRead: () => removeInBulk(actions.removeAllRead()),
				deleteCompleted: () => removeInBulk(actions.removeCompleted()),
			},
		})
	}, [found, toggleReadKeys, removeKeys, markAllRead, removeInBulk, actions])
	useEffect(
		() => () => useNotificationCommands.setState({ commands: null }),
		[],
	)

	return (
		<div className="flex h-full min-h-0 w-full">
			<MetaInfo title={copy.panelTitle} />
			<section
				ref={listPanelRef}
				style={
					isSplit
						? panelWidthStyle(PANEL_SPECS["inbox-list"], listWidth)
						: undefined
				}
				className={cn(
					"relative flex h-full w-full min-w-0 flex-none select-none flex-col",
					isSplit &&
						cn(
							INBOX_LIST_WIDTH_CLASSES,
							"border-r border-gray-200 dark:border-gray-700/80",
						),
				)}
			>
				<div
					className={cn(
						"flex flex-none items-center justify-between gap-2",
						TOP_BAR_HEIGHT,
						TOP_BAR_PADDING,
					)}
				>
					<div
						className={cn(
							"flex min-w-0 items-center gap-1",
							NAV_TOGGLE_CLEARANCE,
						)}
					>
						<h1 className="truncate text-sm font-medium tracking-tight text-primary">
							{copy.panelTitle}
						</h1>
						<DropdownMenu
							trigger={
								<IconButton
									variant="ghost"
									aria-label={copy.actions.more}
									Icon={IconDotsOutline18}
								/>
							}
							contentProps={{ align: "start" }}
							className="w-80"
						>
							<DropdownMenuItem
								Icon={IconCheckDoubleOutline18}
								shortcut={
									<MenuShortcut
										keys={[ShortcutSymbols.ALT, ShortcutSymbols.U]}
									/>
								}
								onSelect={markAllRead}
							>
								{copy.actions.markAllRead}
							</DropdownMenuItem>
							<DropdownMenuSeparator />
							<DropdownMenuItem
								Icon={IconTrashXmarkOutline18}
								onSelect={() => removeInBulk(actions.removeAll())}
							>
								{copy.actions.deleteAll}
							</DropdownMenuItem>
							<DropdownMenuItem
								Icon={IconTrashOutline18}
								shortcut={
									<MenuShortcut
										keys={[ShortcutSymbols.SHIFT, ShortcutSymbols.BACKSPACE]}
									/>
								}
								onSelect={() => removeInBulk(actions.removeAllRead())}
							>
								{copy.actions.deleteRead}
							</DropdownMenuItem>
							<DropdownMenuItem
								Icon={IconCircleCheckOutline18}
								onSelect={() => removeInBulk(actions.removeCompleted())}
							>
								{copy.actions.deleteCompleted}
							</DropdownMenuItem>
							<DropdownMenuSeparator />
							<DropdownMenuItem
								Icon={IconGear2Outline18}
								onSelect={() => void navigate({ href: SETTINGS_PATH })}
							>
								{copy.actions.settings}
							</DropdownMenuItem>
						</DropdownMenu>
					</div>
					<div className="flex flex-none items-center gap-0.5">
						<NotificationFilter />
					</div>
				</div>

				<div className="flex flex-none items-center px-3 pb-2">
					<Tabs
						value={view}
						onValueChange={(value) => {
							filters.setView(value as NotificationListView)
							setLimit(NOTIFICATION_PAGE_SIZE)
						}}
					>
						<TabsList>
							{NOTIFICATION_LIST_VIEWS.map((v) => (
								<TabsTrigger key={v} value={v} count={viewCounts[v]}>
									{copy.views[v]}
								</TabsTrigger>
							))}
						</TabsList>
					</Tabs>
				</div>

				<NotificationFilterChips />

				<div
					role="list"
					className={cn(
						"flex min-h-0 flex-1 flex-col overflow-y-auto pb-2",
						// Room for the selection bar so it never covers the last row.
						checked.size > 0 && "pb-16",
					)}
				>
					<NotificationListContextMenu
						getGroup={getMenuGroup}
						onToggleRead={(g) => toggleReadKeys([g.key])}
						onDelete={(g) => removeKeys([g.key])}
					>
						<div className="contents">
							{groups.map((group) => (
								<NotificationRow
									key={group.key}
									group={group}
									isCursor={group.key === selectedKey}
									isChecked={checked.has(group.key)}
									isSelecting={checked.size > 0}
									onClick={onRowClick}
									onToggleChecked={(g) => toggleChecked(g.key)}
								/>
							))}
						</div>
					</NotificationListContextMenu>

					{!isLoaded && groups.length === 0 && (
						<div className="flex flex-1 items-center justify-center">
							<Loader size={12} />
						</div>
					)}

					{isLoaded && groups.length === 0 && (
						<NotificationListEmpty
							title={hasFilters ? copy.filters.noMatchTitle : copy.empty[view]}
							onShowAll={
								!hasFilters && view !== "inbox"
									? () => {
											filters.setView("inbox")
											setLimit(NOTIFICATION_PAGE_SIZE)
										}
									: undefined
							}
						/>
					)}

					{liveRows.length >= limit && limit < NOTIFICATION_INBOX_MAX_LIMIT && (
						<div className="flex justify-center py-2">
							<Button
								variant="ghost"
								size="sm"
								onClick={() =>
									setLimit((l) =>
										Math.min(
											l + NOTIFICATION_PAGE_SIZE,
											NOTIFICATION_INBOX_MAX_LIMIT,
										),
									)
								}
							>
								{copy.loadMore}
							</Button>
						</div>
					)}
				</div>

				{/* Mounted in both states and faded, so it never pops in. */}
				<div
					className={cn(
						"absolute inset-x-3 bottom-3 flex h-10 items-center gap-0.5 rounded-xl bg-white pl-3.5 pr-1 shadow-lg ring-1 ring-black/5 transition-opacity duration-150 dark:bg-gray-850 dark:ring-white/10",
						checked.size > 0 ? "opacity-100" : "pointer-events-none opacity-0",
					)}
					aria-hidden={checked.size === 0}
				>
					<span className="min-w-0 flex-1 truncate whitespace-nowrap text-[13px] font-medium tabular-nums text-primary">
						{copy.selection.count(checked.size)}
					</span>
					<Tooltip
						label={
							<span className="flex items-center gap-1.5">
								{copy.actions.markRead}
								<Shortcut>U</Shortcut>
							</span>
						}
					>
						<IconButton
							variant="ghost"
							aria-label={copy.actions.markRead}
							Icon={IconEnvelopeOpenOutline18}
							onClick={() => toggleReadKeys([...checked])}
						/>
					</Tooltip>
					<Tooltip
						label={
							<span className="flex items-center gap-1.5">
								{copy.actions.delete}
								<Shortcut>E</Shortcut>
							</span>
						}
					>
						<IconButton
							variant="ghost"
							aria-label={copy.actions.delete}
							Icon={IconTrashOutline18}
							onClick={() => removeKeys([...checked])}
						/>
					</Tooltip>
					<span className="mx-1 h-4 w-px flex-none bg-gray-200 dark:bg-gray-700/80" />
					<Tooltip
						label={
							<span className="flex items-center gap-1.5">
								{copy.selection.clear}
								<Shortcut>Esc</Shortcut>
							</span>
						}
					>
						<IconButton
							variant="ghost"
							aria-label={copy.selection.clear}
							Icon={IconXmarkOutline18}
							onClick={clearSelection}
						/>
					</Tooltip>
				</div>
			</section>

			{isSplit && (
				<PanelResizeGutter
					panel="inbox-list"
					label="Resize notification list"
					getPanel={() => listPanelRef.current}
					getAbsorber={() =>
						detailPanelRef.current?.querySelector<HTMLElement>(
							"[data-thread-conversation]",
						) ?? detailPanelRef.current
					}
				/>
			)}

			{isSplit && (
				<div
					ref={detailPanelRef}
					className="relative flex min-w-0 flex-1 overflow-hidden"
				>
					<NotificationDetail
						group={selected}
						sharedTarget={sharedTarget}
						nav={nav}
						onClose={() => openKey(null)}
					/>
					{!threadOpen && <CopilotOverlay scope={WORKSPACE_COPILOT} />}
				</div>
			)}
		</div>
	)
}
